import { ColDef, ICellRendererParams } from 'ag-grid-community';

import { formatDate } from '~/shared/utils/formatNumber';
import { OptLogVolumeActions } from '~/shared/types/api';
import SizeCell from '~/shared/components/SizeCell';
import { SkeletonCellRenderer } from '~/shared/components/Table/SkeletonCellRenderer';

import VolumeIdCell from './VolumeIdCell';
import TypeCell from './TypeCell';

type OptLogVolumeActionsWithSkeleton = OptLogVolumeActions & { _isSkeleton?: boolean };

export const getOperationColumnDefs = ({
  sortField,
}: {
  sortField: { field: string; dir: 'desc' | 'asc' };
}): ColDef<OptLogVolumeActionsWithSkeleton>[] => [
  {
    headerName: 'Volume ID',
    initialWidth: 260,
    minWidth: 260,
    field: 'volumeId',
    cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
        return <SkeletonCellRenderer width={200} />;
      }
      return data?.volumeId ? <VolumeIdCell id={data.volumeId} /> : null;
    },
    sort: sortField?.field === 'volumeId' ? sortField?.dir : null,
  },
  {
    headerName: 'Instance ID',
    initialWidth: 260,
    minWidth: 260,
    field: 'instanceId',
    sort: sortField?.field === 'instanceId' ? sortField?.dir : null,
    cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
    cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
        return <SkeletonCellRenderer width={150} />;
      }
      return data?.instanceId || null;
    },
  },
  {
    headerName: 'Type',
    initialWidth: 278,
    minWidth: 278,
    maxWidth: 278,
    // initialWidth: 332,
    // minWidth: 332,
    field: 'type',
    cellRenderer: (params: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      const { data, node } = params;
      if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
        return <SkeletonCellRenderer width={200} />;
      }
      if (!data?.type) {
        return null;
      }
      const uniqueId = `${data.volumeId || 'vol'}-${data.actionFinishTime || node.rowIndex}`;
      return (
        <TypeCell
          key={uniqueId}
          type={data.type}
          volumeId={uniqueId}
          progress={data.progress}
          state={data.state}
        />
      );
    },
  },

  {
    headerName: 'Original Size',
    initialWidth: 135,
    minWidth: 135,
    field: 'originalDiskSizeGb',
    // valueFormatter: ({ value }) => `${value} GiB`,
    cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
        return <SkeletonCellRenderer width={100} />;
      }
      return data?.originalDiskSizeGb ? (
        <SizeCell size={data.originalDiskSizeGb} sizeInGiB />
      ) : null;
    },
    sort: sortField?.field === 'originalDiskSizeGb' ? sortField?.dir : null,
  },
  {
    headerName: 'New Size',
    initialWidth: 135,
    minWidth: 135,
    field: 'newDiskSizeGb',
    cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
        return <SkeletonCellRenderer width={100} />;
      }
      return data?.newDiskSizeGb ? <SizeCell size={data.newDiskSizeGb} sizeInGiB /> : null;
    },
    sort: sortField?.field === 'newDiskSizeGb' ? sortField?.dir : null,
  },
  {
    headerName: 'Start Time',
    initialWidth: 160,
    minWidth: 160,
    field: 'actionStartTime',
    cellRenderer: ({ data, value }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={120} />;
      }
      return value ? formatDate(value, true) : null;
    },
    sort: sortField?.field === 'actionStartTime' ? sortField?.dir : null,
    cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
  },
  {
    headerName: 'End Time',
    initialWidth: 160,
    minWidth: 160,
    field: 'actionFinishTime',
    cellRenderer: ({ data, value }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={120} />;
      }
      return data?.state && ['Success', 'Failure', 'Canceled'].includes(data.state)
        ? formatDate(value, true)
        : null;
    },
    sort: sortField?.field === 'actionFinishTime' ? sortField?.dir : null,
    cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
  },
  // {
  //   headerName: 'Account',
  //   initialWidth: 208,
  //   minWidth: 208,
  //   field: 'actionFinishTime',
  //   cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActions>) =>
  //     data?.type ? (
  //       <Typography
  //         variant="p3"
  //         fontWeight={500}
  //         color="text-primary-900"
  //         data-tooltip-id={`${data?.volumeId}_account-column-tooltip`}
  //       >
  //         Loram ipsum
  //       </Typography>
  //     ) : null,
  // },
];
